import React, { createContext, useCallback, useEffect, useState } from "react"
import { Provider } from "react-redux"
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
} from "react-router-dom"
import "./HomeEntry.css"
import { ErrorBoundary } from "./shared/ErrorBoundary"
import { QueryParamProvider } from "use-query-params"
import { request } from "../util/request"
import { Spinner } from "./shared/Spinner"
import { ActionCableProvider } from "react-actioncable-provider"
import { RequestError } from "./shared/RequestError"
import { BandTestScreen } from "./bands/BandTestScreen"
import { BandHighlightsScreen } from "./bands/BandHighlightsScreen"
import HomeScreen from "./home/HomeScreen"
import BandDashboardPage from "../pages/BandDashboardPage"
import ChatScreen from "./chat/ChatScreen"
import BroadcastScreen, {
  GuestBroadcastScreen,
} from "./broadcast/BroadcastScreen"
import {
  Provider as RollbarProvider,
  ErrorBoundary as RollbarErrorBoundary,
} from "@rollbar/react"
import BandEditPage from "../pages/BandEditPage"
import { NewBandScreen } from "./bands/NewBandScreen"
import UserSettingsPage from "../pages/UserSettingsPage/index"
import SearchResultsPage from "../pages/SearchPage"
import UniversalPlayer from "./UniversalPlayer"
import store from "../stores/store"
import PostScreen from "../pages/PostScreen"
import { useQuery } from "react-query"
import ReactGA from "react-ga4"
import { createBrowserHistory } from "history"
import { LiveNowPage } from "./live_now/LiveNowPage"
import DiscoverPage from "../pages/DiscoverPage"
import FaqPage from "./faq/FaqPage"
import OwnBandNadaPage from "./own_bandnada/OwnBandNadaPage"
import ForVenuesPage from "../pages/ForVenuesPage"
import TicketManagementPage from "../pages/TicketManagementPage"
import PayoutDistributionPage from "../pages/PayoutDistributionPage"
import { BandScreen } from "./BandScreen"
import {
  api_v1_user_path,
  band_dashboard_path,
  band_path,
  band_stream_guest_broadcast_path,
  chat_band_path,
  discover_path,
  edit_band_path,
  faq_path,
  go_live_path,
  highlights_band_path,
  live_now_path,
  new_band_path,
  new_event_path,
  new_playlist_path,
  new_post_path,
  own_bandnada_path,
  payout_distribution_path,
  payout_distribution_preview_path,
  root_path,
  search_path,
  test_band_path,
  user_settings_path,
} from "../routes"
import NewPostPage from "../pages/NewPostPage"
import NewPlaylistPage from "../pages/NewPlaylistPage"
import SongActionsSlideout from "./SongActionsSlideout"
import { ToastContainer } from "react-toastify"
import { useRollbar } from "@rollbar/react"
import { AuthDialogContainer } from "./Authentication/AuthDialogContainer"

export const UserContext = createContext({
  user: null,
  spk: undefined,
  player_toggle_delay_secs: null,
  googlek: null,
  scene: null,
  markFeedLoaded: () => {},
})

// Wrapper component to keep both HomeScreen and PostScreen mounted
// This preserves scroll position naturally when navigating between them
function FeedAndPostWrapper({ children }) {
  const location = useLocation()
  const isHomePage = location.pathname === root_path()
  // Route is /p/:post_id, not /posts/:id
  const isPostPage = location.pathname.match(/^\/p\//)
  const isHomeOrPost = isHomePage || isPostPage

  return (
    <>
      {/* Always render HomeScreen (keeps it mounted to preserve scroll position) */}
      {/* Hide when not on home page using display: none */}
      {/* Note: HomeScreen has its own id="home-screen-container" on its root div */}
      <div
        style={{
          display: isHomePage ? "block" : "none",
          position: isHomePage ? "relative" : "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
        }}
      >
        <HomeScreen />
      </div>

      {/* Always render PostScreen when on post route (keeps it mounted to preserve state) */}
      {/* Hide when not on post using display: none */}
      {/* Use key based on pathname to force re-render when navigating between posts */}
      {isPostPage && (
        <div
          style={{
            display: isPostPage ? "block" : "none",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "auto",
            backgroundColor: "white",
            zIndex: 10,
          }}
          id="page-scroll-container"
        >
          <PostScreen key={location.pathname} />
        </div>
      )}

      {/* Render other routes normally through Switch, but only if not on home or post */}
      {!isHomeOrPost && children}
    </>
  )
}

export default function HomeApp({
  env,
  host,
  spk,
  player_toggle_delay_secs,
  googlek,
  gaid,
  is_summary_screen,
  rollbar_key,
}) {
  const userData = useQuery(["user", { path: api_v1_user_path(1) }], request)
  const user = userData?.data?.user
  
  const band = user?.band
  const wsURL =
    env === "development" ? `ws://${host}/cable` : `wss://${host}/cable`

  const scene = user?.scene_list || "new york city"

  // BandsFeed (the home page's primary content) stays mounted at all times
  // (see FeedAndPostWrapper) and reports in here once its own data has
  // loaded, so the signal below doesn't fire while it's still showing a
  // spinner - see markFeedLoaded.
  const [feedReady, setFeedReady] = useState(false)
  const markFeedLoaded = useCallback(() => setFeedReady(true), [])

  // Signals to bin/prerender.js that it's safe to capture the page - past
  // the initial user-fetch loading gate and the feed's own data load, with
  // real route content rendered instead of just a spinner. Must run before
  // any early return (Rules of Hooks), so it lives up here rather than near
  // them.
  useEffect(() => {
    if (userData.status === "success" && feedReady) {
      document.body.dataset.prerenderReady = "true"
    }
  }, [userData.status, feedReady])

  if (env === "production" && gaid) {
    ReactGA.initialize(gaid)
    ReactGA.set({
      userId: user?.id,
      // add other data here
    })
    const history = createBrowserHistory()
    history.listen((location) => {
      ReactGA.set({ page: location.pathname }) // Update the user's current page
      ReactGA.send("pageview") // Record a pageview for the given page
    })
  }

  if (userData.status === "loading") {
    return <Spinner className="text-gray-500" />
  }
  if (userData.status === "error") {
    return <RequestError error={userData.error} />
  }

  const rollbarConfig = {
    accessToken: rollbar_key,
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: env,
    enabled: env === "production",
    payload: {
      person: {
        id: user?.id,
        email: user?.email,
        name: user?.name,
      },
    },
  }

  return (
    <RollbarProvider config={rollbarConfig}>
      <RollbarErrorBoundary>
        <Provider store={store}>
          <ErrorBoundary>
            <Router>
              <QueryParamProvider ReactRouterRoute={Route}>
                <ActionCableProvider url={wsURL}>
                  <UserContext.Provider
                    value={{
                      user,
                      spk,
                      player_toggle_delay_secs,
                      googlek,
                      scene,
                      markFeedLoaded,
                    }}
                  >
                    <FeedAndPostWrapper>
                      <Switch>
                        {/* HomeScreen and PostScreen are handled by FeedAndPostWrapper */}
                        {/* They're kept mounted for smooth scroll restoration */}

                        <Route path={new_event_path()} exact={false}>
                          {userData.status === "loading" ? (
                            <Spinner className="text-gray-500" />
                          ) : user ? (
                            <NewPostPage user={user} isEvent={true} />
                          ) : (
                            <Redirect
                              from={new_event_path()}
                              to={root_path()}
                            />
                          )}
                        </Route>

                        <Route path={new_post_path()} exact={true}>
                          {userData.status === "loading" ? (
                            <Spinner className="text-gray-500" />
                          ) : user ? (
                            <NewPostPage user={user} isEvent={false} />
                          ) : (
                            <Redirect from={new_post_path()} to={root_path()} />
                          )}
                        </Route>

                        <Route path={new_playlist_path()} exact={true}>
                          {userData.status === "loading" ? (
                            <Spinner className="text-gray-500" />
                          ) : user ? (
                            <NewPlaylistPage user={user} />
                          ) : (
                            <Redirect from={new_playlist_path()} to={root_path()} />
                          )}
                        </Route>

                        <Route
                          path={chat_band_path(":custom_subdomain")}
                          exact={true}
                        >
                          <ChatScreen user={user} spk={spk} />
                        </Route>

                        <Route
                          path={test_band_path(":custom_subdomain")}
                          exact={false}
                        >
                          <BandTestScreen user={user} spk={spk} />
                        </Route>

                        <Route
                          path={edit_band_path(":custom_subdomain")}
                          exact={false}
                        >
                          {user ? (
                            <BandEditPage />
                          ) : (
                            <Redirect
                              from={edit_band_path(":custom_subdomain")}
                              to={root_path()}
                            />
                          )}
                        </Route>

                        <Route
                          path={highlights_band_path(":custom_subdomain")}
                          exact={false}
                        >
                          <BandHighlightsScreen user={user} />
                        </Route>

                        <Route path={new_band_path()} exact={false}>
                          <NewBandScreen user={user} band={band} />
                        </Route>

                        <Route
                          path={band_path(":custom_subdomain")}
                          exact={false}
                        >
                          {is_summary_screen ? (
                            <BandHighlightsScreen user={user} />
                          ) : (
                            <BandScreen />
                          )}
                        </Route>

                        <Route path="/dashboard/payouts/:payout_id" exact>
                          {user ? (
                            <PayoutDistributionPage />
                          ) : (
                            <Redirect to="/" />
                          )}
                        </Route>

                        <Route path={band_dashboard_path()}>
                          {user && user.band ? (
                            <BandDashboardPage bandId={user.band_id} />
                          ) : (
                            <Redirect to="/" />
                          )}
                        </Route>

                        <Route path={user_settings_path()}>
                          {user ? (
                            <UserSettingsPage user={user} />
                          ) : (
                            <Redirect to="/" />
                          )}
                        </Route>

                        <Route path={go_live_path()} exact={true}>
                          {user ? <BroadcastScreen /> : <Redirect to="/" />}
                        </Route>

                        <Route
                          path={band_stream_guest_broadcast_path(":id")}
                          exact={false}
                        >
                          <GuestBroadcastScreen user={user} />
                        </Route>

                        <Route path={search_path()}>
                          <SearchResultsPage user={user} />
                        </Route>

                        <Route path={discover_path()}>
                          <DiscoverPage />
                        </Route>

                        <Route path={live_now_path()}>
                          <LiveNowPage />
                        </Route>

                        <Route path={faq_path()}>
                          <FaqPage />
                        </Route>

                        <Route path={own_bandnada_path()}>
                          <OwnBandNadaPage />
                        </Route>

                        <Route path="/for-venues-and-promoters">
                          <ForVenuesPage />
                        </Route>

                        <Route path="/manage-tickets/:postId">
                          {user ? (
                            <TicketManagementPage />
                          ) : (
                            <Redirect to="/" />
                          )}
                        </Route>

                        <Route path={payout_distribution_preview_path(":post_id")} exact>
                          {user ? (
                            <PayoutDistributionPage />
                          ) : (
                            <Redirect to="/" />
                          )}
                        </Route>

                        <Route>
                          <div>Not found</div>
                        </Route>
                      </Switch>
                    </FeedAndPostWrapper>
                    <SongActionsSlideout />
                    <AuthDialogContainer />
                  </UserContext.Provider>
                </ActionCableProvider>
              </QueryParamProvider>
              <UniversalPlayer />
              <ToastContainer />
            </Router>
          </ErrorBoundary>
        </Provider>
      </RollbarErrorBoundary>
    </RollbarProvider>
  )
}
